import * as THREE from 'three';
import { MATERIAL_BY_ID } from '../data/materials.js';

const PROCEDURAL_TYPES = new Set(['roof', 'wood', 'concrete', 'stone', 'ceramic', 'grass', 'paving']);
const TEXTURE_SIZE = 32;

function textureTone(type, x, y) {
  const noise = ((x * 17 + y * 29 + x * y * 3) % 23) - 11;
  if (type === 'roof') {
    const row = Math.floor(y / 6);
    if (y % 6 === 0 || (x + (row % 2) * 6) % 12 === 0) return 176;
    return 239 + Math.floor(noise * 0.32);
  }
  if (type === 'wood') {
    if (x % 8 === 0) return 188;
    if ((y + x * 2) % 17 === 0) return 218;
    return 242 + Math.floor(noise * 0.48);
  }
  if (type === 'ceramic') return x % 16 === 0 || y % 16 === 0 ? 184 : 248 + Math.floor(noise * 0.18);
  if (type === 'paving') return x % 8 === 0 || (y + (Math.floor(x / 8) % 2) * 4) % 8 === 0 ? 188 : 238 + Math.floor(noise * 0.38);
  if (type === 'stone') return (x + Math.floor(y / 5) * 3) % 11 === 0 || y % 9 === 0 ? 190 : 232 + Math.floor(noise * 0.62);
  if (type === 'grass') return 218 + noise * 2;
  return 237 + noise;
}

export class MaterialFactory {
  #cache = new Map();
  #textures = new Map();

  get(materialId = 'wall-warm', overrides = {}) {
    const definition = MATERIAL_BY_ID.get(materialId) ?? MATERIAL_BY_ID.get('wall-warm');
    const settings = { ...definition, ...overrides };
    const patternType = PROCEDURAL_TYPES.has(settings.type) ? settings.type : null;
    const key = [materialId, settings.color, settings.roughness, settings.metalness, settings.opacity ?? 1, patternType ?? 'plain'].join(':');
    if (this.#cache.has(key)) return this.#cache.get(key);
    const material = new THREE.MeshStandardMaterial({
      name: materialId,
      color: settings.color,
      map: patternType ? this.#getTexture(patternType) : null,
      roughness: settings.roughness ?? 0.8,
      metalness: settings.metalness ?? 0,
      transparent: (settings.opacity ?? 1) < 1,
      opacity: settings.opacity ?? 1,
      side: settings.type === 'glass' || settings.type === 'water' ? THREE.DoubleSide : THREE.FrontSide,
      depthWrite: (settings.opacity ?? 1) > 0.6
    });
    material.userData.registryKey = key;
    this.#cache.set(key, material);
    return material;
  }

  dispose() {
    for (const material of this.#cache.values()) material.dispose();
    for (const texture of this.#textures.values()) texture.dispose();
    this.#cache.clear();
    this.#textures.clear();
  }

  #getTexture(type) {
    if (this.#textures.has(type)) return this.#textures.get(type);
    const data = new Uint8Array(TEXTURE_SIZE * TEXTURE_SIZE * 4);
    for (let y = 0; y < TEXTURE_SIZE; y += 1) {
      for (let x = 0; x < TEXTURE_SIZE; x += 1) {
        const offset = (y * TEXTURE_SIZE + x) * 4;
        const tone = Math.max(96, Math.min(255, textureTone(type, x, y)));
        data[offset] = tone;
        data[offset + 1] = tone;
        data[offset + 2] = tone;
        data[offset + 3] = 255;
      }
    }
    const texture = new THREE.DataTexture(data, TEXTURE_SIZE, TEXTURE_SIZE, THREE.RGBAFormat, THREE.UnsignedByteType);
    const repeats = {
      roof: [4, 4], wood: [4, 3], concrete: [3, 3], stone: [3, 3], ceramic: [4, 4], grass: [6, 6], paving: [7, 7]
    };
    texture.name = `KIPS procedural ${type}`;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(...(repeats[type] ?? [3, 3]));
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.needsUpdate = true;
    this.#textures.set(type, texture);
    return texture;
  }
}
